import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';
import { setTokens, logout } from '../slices/authSlice';
import { tokenFromStorage, withRefreshLock } from '../session-lock';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.accessToken;
    const tenantDomain = state.auth.tenantDomain;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (tenantDomain) {
      headers.set('X-Tenant-Domain', tenantDomain);
    }
    return headers;
  },
});

/**
 * Eine Erneuerung zur Zeit — innerhalb einer Seite und über alle Reiter.
 *
 * Eine Seite stellt beim Öffnen ein halbes Dutzend Anfragen. Ist das
 * Zugangstoken gerade abgelaufen, antworten alle mit 401 — und ohne diese
 * Sperre liefe für jede einzelne eine eigene Erneuerung los, alle mit
 * demselben Erneuerungstoken. Der Dienst dreht das Token bei jeder Erneuerung
 * weiter und erkennt die zweite Vorlage desselben Tokens als Wiederverwendung:
 * Dann wird die ganze Token-Familie widerrufen, auch das eben erst
 * ausgegebene. Aus sechs harmlosen Anfragen wird eine Abmeldung — und in zwei
 * offenen Reitern stirbt auch der, der alles richtig gemacht hat.
 *
 * Das Versprechen unten hält die Anfragen *einer* Seite zusammen. Über Reiter
 * hinweg reicht es nicht: Jeder Reiter hat seinen eigenen Speicher. Dafür gibt
 * es `navigator.locks` — eine Sperre je Ursprung, die alle Reiter teilen.
 */
let laufendeErneuerung: Promise<boolean> | null = null;

/**
 * Anfragen, bei denen eine 401 kein abgelaufenes Zugangstoken bedeutet, sondern
 * die Antwort selbst ist: falsches Passwort, falscher Code, ein
 * Erneuerungstoken, das der Dienst nicht mehr kennt. Sie gehen an der
 * Erneuerung vorbei — sie legen ja gerade selbst Zugangsdaten vor.
 *
 * Das ist keine Kosmetik. ProtectedRoute erneuert beim Neuladen der Seite
 * unter der Sperre, und zwar über diese Funktion. Bekam die Erneuerung eine
 * 401, wurde daraus hier eine *zweite* Erneuerung — und die wartete auf die
 * Sperre, die ihr Aufrufer hält. Wer nach mehr als sieben Tagen wiederkam,
 * sah statt der Anmeldeseite für immer den Ladekreis (25. September 2026).
 */
const OHNE_ERNEUERUNG = ['/auth/login', '/auth/refresh', '/auth/mfa/verify'];
const pfadVon = (args: string | FetchArgs): string => (typeof args === 'string' ? args : args.url);

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  if (OHNE_ERNEUERUNG.some((pfad) => pfadVon(args).startsWith(pfad))) {
    return baseQuery(args, api, extraOptions);
  }

  // Wartet eine Erneuerung, wartet auch diese Anfrage — sonst ginge sie mit
  // dem Token hinaus, das gerade ersetzt wird, und käme als 401 zurück.
  if (laufendeErneuerung) await laufendeErneuerung;

  let result = await baseQuery(args, api, extraOptions);
  if (!result.error || result.error.status !== 401) return result;

  if (!laufendeErneuerung) {
    const vorher = (api.getState() as RootState).auth.refreshToken;
    laufendeErneuerung = withRefreshLock(async () => {
      // Innerhalb der Sperre nachsehen: Hat ein anderer Reiter inzwischen
      // erneuert, liegt sein frisches Token im Speicher. Es noch einmal
      // vorzulegen wäre genau die Wiederverwendung, die alle Sitzungen beendet.
      const imSpeicher = tokenFromStorage('refreshToken');
      if (imSpeicher && imSpeicher !== vorher) {
        api.dispatch(
          setTokens({ accessToken: tokenFromStorage('accessToken') ?? '', refreshToken: imSpeicher }),
        );
        return true;
      }

      const refreshToken = imSpeicher ?? vorher;
      if (!refreshToken) {
        api.dispatch(logout());
        return false;
      }
      const refreshResult = await baseQuery(
        { url: '/auth/refresh', method: 'POST', body: { refreshToken } },
        api,
        extraOptions
      );
      if (!refreshResult.data) {
        api.dispatch(logout());
        return false;
      }
      api.dispatch(setTokens(refreshResult.data as { accessToken: string; refreshToken: string }));
      return true;
    }).finally(() => {
      laufendeErneuerung = null;
    });
  }

  // Festgehalten, nicht noch einmal gelesen: Zwischen dem Setzen oben und
  // dieser Zeile liegt heute kein `await`, die Variable kann also nicht schon
  // wieder null sein. Wer hier später eines einfügt, soll aber nicht auf null
  // warten und die 401 durchreichen.
  const erneuerung = laufendeErneuerung;
  return (await erneuerung) ? baseQuery(args, api, extraOptions) : result;
};

/**
 * Nachladen beim Zurückkehren in den Reiter und nach einer Unterbrechung der
 * Verbindung.
 *
 * Testprotokoll TG 2.4, T33: Die seit 07:43 offene Seite im Profil der
 * Prüfungsleitung zeigte im Reiter „Arbeitspapiere“ noch den Stand von 07:43;
 * erst nach Neuladen standen dort alle sieben Arbeitspapiere, die eine andere
 * Person inzwischen eingereicht hatte. Abgerufene Daten blieben, bis eine
 * eigene Änderung sie ungültig machte. Jetzt holt jede offene Abfrage ihre
 * Daten neu, sobald das Fenster wieder den Fokus bekommt oder die Verbindung
 * zurück ist (setupListeners in store/index.ts).
 *
 * Offene Formulare bleiben, wie sie sind: Sind die Daten unverändert, behält
 * RTK Query dieselben Objekte, und kein Formular wird neu befüllt; der
 * Berichtseditor übernimmt geänderte Daten nur ohne ungespeicherte Eingaben.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: [
    'Auth',
    'Tenants',
    'ApprovalGates',
    'Ai',
    'Users',
    'Role',
    'AuditUniverse',
    'Risk',
    'Planning',
    'Engagements',
    'AuditFields',
    'Findings',
    'Reports',
    'Measures',
    'Dashboard',
    'Governance',
    'Notifications',
    'Documents',
    'Search',
    'Snippets',
    'BusinessProcesses',
    'WorkPapers',
    'TimeEntries',
    'Conformity',
    'ConformityCatalogue',
    'AuditTrail',
    'GdprDeletion',
  ],
  endpoints: () => ({}),
});

/**
 * Für Abfragen, die einen Klick beantworten — eine Vorlage, ein Export, eine
 * Prüfung der Kette: Sie werden beim Zurückkehren in den Reiter nicht
 * wiederholt. `useLazy…Query(NUR_AUF_ABRUF)`.
 */
export const NUR_AUF_ABRUF = { refetchOnFocus: false, refetchOnReconnect: false } as const;
